import React from "react";
import { TextField } from "Components/Base/Inputs/TextField";
import { InputAdornment } from "Components/Base/Inputs/InputAdornment";
import { IconButton } from "Components/Base/Inputs/IconButton";
import { Clear as ClearIcon, Search as SearchIcon } from "Components/Icons";
import styled from "styled-components";

/**
 * SearchField Component
 *
 * A custom search field component with an integrated clear button and search icon.
 *
 * @component
 * @example
 * // Example usage:
 * // import SearchField from "./SearchField";
 * // <SearchField
 * //   placeholder="Search..."
 * //   ariaLabel="Search Input"
 * //   clearLabel="Clear"
 * //   value={searchValue}
 * //   onChange={handleSearchChange}
 * //   onClear={handleClearSearch}
 * // />
 *
 * @param {string} props.placeholder - The placeholder text for the search input.
 * @param {string} [props.ariaLabel] - The ARIA label for the search input (default is set to the placeholder).
 * @param {string} props.clearLabel - The label for the clear button.
 * @param {string} props.value - The current value of the search input.
 * @param {function} props.onChange - The callback function triggered on search input change.
 * @param {function} props.onClear - The callback function triggered when the clear button is clicked.
 *
 * @returns {JSX.Element} JSX element representing the SearchField component.
 */

const StyledSearchIconTextField = styled(TextField)`
  .MuiInputBase-root {
    padding-right: 4px !important;
  }
`;

const SearchField = ({
  placeholder,
  ariaLabel,
  clearLabel,
  value,
  onChange,
  onClear,
}) => {
  return (
    <StyledSearchIconTextField
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      value={value}
      onChange={onChange}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              ariaLabel={value ? clearLabel : placeholder}
              onClick={onClear}
              icon={value ? <ClearIcon /> : <SearchIcon />}
              disabled={!value}
              color="primary"
            />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchField;
